import { useUpdateIntegration } from '@modules/integrationsV2/hooks';
import { EditIntegrationDrawerContentProps } from '@modules/integrationsV2/types';
import { trackEditIntegrationEvent } from '@modules/integrationsV2/utils';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IntegrationChannelType, QualtricsSurvey } from 'generated/graphql';
import { useState } from 'react';
import useGetIntegrationEditFormData from 'v2/core/repository/edge-server/integrations/editIntegrations/useGetIntegrationEditFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { AddOrEditIntegrationDrawerHeader } from '../../AddOrEditIntegrationDrawerHeader';

export const EditQualtricsIntegrationDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationData,
  integrationType,
}: EditIntegrationDrawerContentProps) => {
  const [selectedSurveys, setSelectedSurveys] = useState<QualtricsSurvey[]>(
    integrationData.qualtrics?.surveys ?? [],
  );

  const { data: integrationFormData, status: integrationFormStatus } =
    useGetIntegrationEditFormData({ integrationID });

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Qualtrics,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
        trackEditIntegrationEvent({
          eventName: 'Edit Integration Submitted',
          channelType: IntegrationChannelType.Qualtrics,
          integrationID,
        });
      },
    });

  const handleSubmit = () => {
    updateIntegration({
      integrationID,
      isActive: true,
      updateData: {
        qualtrics: {
          surveys: selectedSurveys,
        },
      },
    });
  };

  return (
    <>
      <AddOrEditIntegrationDrawerHeader title="Edit Qualtrics Integration" />
      <Stack p={3} spacing={3}>
        <Typography>
          Select the surveys you'd like Enterpret to ingest from your Qualtrics
          account
        </Typography>
        <Autocomplete
          fullWidth
          multiple
          limitTags={10}
          disableCloseOnSelect
          options={integrationFormData?.qualtrics?.surveys ?? []}
          value={selectedSurveys}
          getOptionLabel={(option) => option.name}
          onChange={(_, data) => setSelectedSurveys(data)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select surveys" />
          )}
          renderOption={(props, option, { selected }) => {
            return (
              <Box {...props} component="li" style={{ padding: '2px' }}>
                <Stack alignItems="center" direction="row">
                  <Checkbox size="small" checked={selected} />
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body1">{option.name}</Typography>
                  </Stack>
                </Stack>
              </Box>
            );
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          componentsProps={{ paper: { elevation: 8 } }}
          loading={IsLoading(integrationFormStatus)}
        />
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          onClick={handleSubmit}
          loading={IsLoading(updateIntegrationStatus)}
        >
          Save Changes
        </LoadingButton>
      </Stack>
    </>
  );
};
